"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import dayjs from "dayjs";
import { type ColumnDef } from "@tanstack/react-table";
import TableComponent from "@components/ui/table";
import { Card, CardContent, CardHeader } from "@tracify/ui/card";
import { Button } from "@tracify/ui/button";
import { Input } from "@tracify/ui/input";
import { Label } from "@tracify/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tracify/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@tracify/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@tracify/ui/radio-group";
import { toast } from "sonner";
import {
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon as CheckCircleIconSolid } from "@heroicons/react/24/solid";
import { Separator } from "@tracify/ui/separator";
import { Tooltip, TooltipContent, TooltipTrigger } from "@tracify/ui/tooltip";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import { useShopIntegrations } from "@lib/api-hooks/useShopIntegrations";
import {
  useDecifyCredentials,
  useUpsertDecifyCredentials,
  useDeleteDecifyCredentials,
  useDecifySurveys,
  useDecifyProjects,
  useCreateDecifyProject,
  usePatchDecifyProject,
  useDeleteDecifyProject,
  useSyncDecifyBlocks,
  useDecifyBlocks,
  useDecifyMappings,
  useCreateDecifyMapping,
  useDeleteDecifyMapping,
  usePatchDecifyMapping,
  useDecifyAppInfo,
  type DecifyProject,
  type DecifyMapping,
} from "@lib/api-hooks/useDecify/useDecify";

const TRACIFY_SOURCES = [
  { label: "Meta", value: "facebook" },
  { label: "Google", value: "google" },
  { label: "TikTok", value: "tiktok" },
  { label: "Pinterest", value: "pinterest" },
  { label: "Microsoft Ads", value: "msads" },
  { label: "Snapchat", value: "snapchat" },
  { label: "Taboola", value: "taboola" },
  { label: "Outbrain", value: "outbrain" },
  { label: "Criteo", value: "criteo" },
  { label: "Influencer", value: "influencer" },
  { label: "Email", value: "email" },
  { label: "WhatsApp", value: "whatsapp" },
  { label: "Organic", value: "referred" },
  { label: "Direct", value: "direct" },
];

function findTracifySource(answerValue: string): string | null {
  const normalized = answerValue.trim().toLowerCase();
  const exact = TRACIFY_SOURCES.find(
    (s) =>
      s.label.toLowerCase() === normalized ||
      s.value.toLowerCase() === normalized
  );
  if (exact) return exact.value;
  const partial = TRACIFY_SOURCES.find(
    (s) =>
      normalized.includes(s.label.toLowerCase()) ||
      normalized.includes(s.value.toLowerCase())
  );
  return partial?.value ?? null;
}

function getTracifySourceLabel(value: string): string {
  return TRACIFY_SOURCES.find((s) => s.value === value)?.label ?? value;
}

function formatChannelName(name: string): string {
  return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// --- Credentials section ---

function CredentialsSection({
  csid,
  hasIntegrationError,
  disabled,
}: {
  csid: string;
  hasIntegrationError: boolean;
  disabled?: boolean;
}) {
  const { data: creds, isLoading, isError } = useDecifyCredentials(csid);
  const upsert = useUpsertDecifyCredentials();
  const remove = useDeleteDecifyCredentials();
  const [keyId, setKeyId] = useState("");
  const [secret, setSecret] = useState("");
  const [editing, setEditing] = useState(false);

  const handleSave = async () => {
    if (!keyId || !secret) {
      toast.error("Both Access Key ID and Secret are required.");
      return;
    }
    try {
      await upsert.mutateAsync({
        csid,
        access_key_id: keyId,
        access_key_secret: secret,
      });
      toast.success("Credentials saved.");
      setKeyId("");
      setSecret("");
      setEditing(false);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 400) {
        toast.error(
          "Invalid credentials — check your Access Key ID and Secret."
        );
      } else if (status === 502) {
        toast.error("Decify is unreachable. Please try again later.");
      } else {
        toast.error("Failed to save credentials.");
      }
    }
  };

  const handleDelete = async () => {
    try {
      await remove.mutateAsync(csid);
      toast.success("Credentials removed.");
    } catch {
      toast.error("Failed to remove credentials.");
    }
  };

  const hasCredentials = !isError && !!creds;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">API Credentials</h3>
        <p className="text-sm text-muted-foreground mt-0.5">
          Decify access keys for store{" "}
          <span className="font-mono text-xs">{csid}</span>
        </p>
      </div>
      {hasCredentials && !editing ? (
        <div className="flex items-center justify-between rounded-lg border bg-muted/40 px-4 py-3">
          <div className="text-sm text-muted-foreground">
            Key ID:{" "}
            <span className="font-mono text-foreground">
              {creds.access_key_id}
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing(true)}
              disabled={disabled}
            >
              Update
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDelete}
              disabled={disabled}
              className="text-destructive hover:text-destructive"
            >
              <TrashIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Access Key ID</Label>
              <Input
                variant="alternate"
                placeholder="69d79106903f..."
                value={keyId}
                onChange={(e) => setKeyId(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Secret Access Key</Label>
              <Input
                variant="alternate"
                type="password"
                placeholder="••••••••"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={upsert.isPending || disabled}
            >
              {upsert.isPending ? "Saving…" : "Save credentials"}
            </Button>
            {editing && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditing(false)}
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Add mapping dialog ---

function AddMappingDialog({
  open,
  onOpenChange,
  project,
  answerChoices,
  mappedAnswers,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: DecifyProject;
  answerChoices: string[];
  mappedAnswers: string[];
}) {
  const createMapping = useCreateDecifyMapping();
  const [answerValue, setAnswerValue] = useState("");
  const [type, setType] = useState<"ad_source" | "custom_label">("ad_source");
  const [channelValue, setChannelValue] = useState("");

  const reset = () => {
    setAnswerValue("");
    setType("ad_source");
    setChannelValue("");
  };

  const handleAdd = async () => {
    if (!answerValue || !channelValue) {
      toast.error("All fields are required.");
      return;
    }
    try {
      await createMapping.mutateAsync({
        projectId: project.id,
        csid: project.csid,
        answer_value: answerValue,
        ...(type === "ad_source"
          ? { tracify_source: channelValue }
          : {
              virtual_channel_name: formatChannelName(channelValue.trim()),
            }),
      });
      toast.success("Mapping added.");
      reset();
      onOpenChange(false);
    } catch {
      toast.error(
        "Failed to add mapping. This answer value may already be mapped."
      );
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Add channel mapping</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-1.5">
            <Label>Survey answer</Label>
            {answerChoices.length > 0 ? (
              <Select value={answerValue} onValueChange={setAnswerValue}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an answer…" />
                </SelectTrigger>
                <SelectContent>
                  {answerChoices.map((choice) => {
                    const alreadyMapped = mappedAnswers.includes(choice);
                    return (
                      <SelectItem
                        key={choice}
                        value={choice}
                        disabled={alreadyMapped}
                      >
                        <span
                          className={
                            alreadyMapped
                              ? "text-muted-foreground line-through"
                              : undefined
                          }
                        >
                          {choice}
                        </span>
                        {alreadyMapped && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            mapped
                          </span>
                        )}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            ) : (
              <Input
                placeholder='e.g. "Google"'
                value={answerValue}
                onChange={(e) => setAnswerValue(e.target.value)}
              />
            )}
            <p className="text-xs text-muted-foreground">
              The exact text of the answer respondents select in the survey.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Type</Label>
            <RadioGroup
              value={type}
              onValueChange={(v) => {
                setType(v as "ad_source" | "custom_label");
                setChannelValue("");
              }}
              className="grid grid-cols-2 gap-2"
            >
              <label
                className={`flex flex-col gap-1 rounded-md border p-3 cursor-pointer transition-colors ${
                  type === "ad_source"
                    ? "border-primary bg-primary/5"
                    : "border-border"
                }`}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="ad_source" id="type-ad" />
                  <span className="text-sm font-medium">Ad source</span>
                </div>
                <span className="text-xs text-muted-foreground pl-6">
                  Maps to a tracked Tracify source
                </span>
              </label>
              <label
                className={`flex flex-col gap-1 rounded-md border p-3 cursor-pointer transition-colors ${
                  type === "custom_label"
                    ? "border-primary bg-primary/5"
                    : "border-border"
                }`}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="custom_label" id="type-virtual" />
                  <span className="text-sm font-medium">Custom label</span>
                </div>
                <span className="text-xs text-muted-foreground pl-6">
                  Free-form name for grouping
                </span>
              </label>
            </RadioGroup>
          </div>

          <div className="space-y-1.5">
            {type === "ad_source" ? (
              <>
                <Label>Tracify source</Label>
                <Select value={channelValue} onValueChange={setChannelValue}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a source…" />
                  </SelectTrigger>
                  <SelectContent>
                    {TRACIFY_SOURCES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            ) : (
              <>
                <Label>Label</Label>
                <Input
                  placeholder="word_of_mouth"
                  value={channelValue}
                  onChange={(e) => setChannelValue(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  A free-form name used to group responses, e.g.{" "}
                  <span className="font-mono">word_of_mouth</span>
                </p>
              </>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleAdd} disabled={createMapping.isPending}>
            {createMapping.isPending ? "Adding…" : "Add mapping"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Edit mapping dialog ---

function EditMappingDialog({
  open,
  onOpenChange,
  project,
  mapping,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: DecifyProject;
  mapping: DecifyMapping;
}) {
  const patchMapping = usePatchDecifyMapping();
  const initialType = mapping.tracify_source ? "ad_source" : "custom_label";
  const initialChannel =
    mapping.tracify_source ?? mapping.virtual_channel_name ?? "";
  const [type, setType] = useState<"ad_source" | "custom_label">(initialType);
  const [channelValue, setChannelValue] = useState(initialChannel);

  // Reset state when dialog opens with a (possibly different) mapping
  useEffect(() => {
    if (open) {
      setType(mapping.tracify_source ? "ad_source" : "custom_label");
      setChannelValue(
        mapping.tracify_source ?? mapping.virtual_channel_name ?? ""
      );
    }
  }, [open, mapping]);

  const handleSave = async () => {
    if (!channelValue) {
      toast.error("Channel value is required.");
      return;
    }
    try {
      const payload =
        type === "ad_source"
          ? { tracify_source: channelValue, virtual_channel_name: null }
          : {
              tracify_source: null,
              virtual_channel_name: formatChannelName(channelValue.trim()),
            };
      await patchMapping.mutateAsync({
        projectId: project.id,
        csid: project.csid,
        mappingId: mapping.id,
        ...payload,
      });
      toast.success("Mapping updated.");
      onOpenChange(false);
    } catch {
      toast.error("Failed to update mapping.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Edit channel mapping</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-1.5">
            <Label>Survey answer</Label>
            <div className="flex h-9 w-full rounded-md border border-input bg-muted px-3 py-1 text-sm items-center font-mono">
              {mapping.answer_value}
            </div>
            <p className="text-xs text-muted-foreground">
              The survey answer cannot be changed after creation.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Type</Label>
            <RadioGroup
              value={type}
              onValueChange={(v) => {
                setType(v as "ad_source" | "custom_label");
                setChannelValue("");
              }}
              className="grid grid-cols-2 gap-2"
            >
              <label
                className={`flex flex-col gap-1 rounded-md border p-3 cursor-pointer transition-colors ${
                  type === "ad_source"
                    ? "border-primary bg-primary/5"
                    : "border-border"
                }`}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="ad_source" id="edit-type-ad" />
                  <span className="text-sm font-medium">Ad source</span>
                </div>
                <span className="text-xs text-muted-foreground pl-6">
                  Maps to a tracked Tracify source
                </span>
              </label>
              <label
                className={`flex flex-col gap-1 rounded-md border p-3 cursor-pointer transition-colors ${
                  type === "custom_label"
                    ? "border-primary bg-primary/5"
                    : "border-border"
                }`}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="custom_label" id="edit-type-virtual" />
                  <span className="text-sm font-medium">Custom label</span>
                </div>
                <span className="text-xs text-muted-foreground pl-6">
                  Free-form name for grouping
                </span>
              </label>
            </RadioGroup>
          </div>

          <div className="space-y-1.5">
            {type === "ad_source" ? (
              <>
                <Label>Tracify source</Label>
                <Select value={channelValue} onValueChange={setChannelValue}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a source…" />
                  </SelectTrigger>
                  <SelectContent>
                    {TRACIFY_SOURCES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            ) : (
              <>
                <Label>Label</Label>
                <Input
                  placeholder="word_of_mouth"
                  value={channelValue}
                  onChange={(e) => setChannelValue(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  A free-form name used to group responses, e.g.{" "}
                  <span className="font-mono">word_of_mouth</span>
                </p>
              </>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={patchMapping.isPending}>
            {patchMapping.isPending ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Mappings table ---

function MappingsSection({ project }: { project: DecifyProject }) {
  const { data: mappings = [], isLoading } = useDecifyMappings(
    project.id,
    project.csid
  );
  const { data: blocks = [], isLoading: blocksLoading } = useDecifyBlocks(
    project.id,
    project.csid
  );
  const deleteMapping = useDeleteDecifyMapping();
  const createMapping = useCreateDecifyMapping();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState<DecifyMapping | null>(
    null
  );
  const [autoAssigning, setAutoAssigning] = useState(false);
  const hasAutoAssigned = useRef(false);

  // Collect all choices from multiple_choice blocks, deduplicated
  const answerChoices = Array.from(new Set(blocks.flatMap((b) => b.choices)));

  const mappedAnswers = mappings.map((m) => m.answer_value);
  const unmappedChoices = answerChoices.filter(
    (c) => !mappedAnswers.includes(c)
  );

  const handleDelete = useCallback(
    async (mappingId: number) => {
      try {
        await deleteMapping.mutateAsync({
          projectId: project.id,
          csid: project.csid,
          mappingId,
        });
        toast.success("Mapping removed.");
      } catch {
        toast.error("Failed to remove mapping.");
      }
    },
    [deleteMapping, project.csid, project.id]
  );

  const handleAutoAssign = async () => {
    if (unmappedChoices.length === 0) return;
    setAutoAssigning(true);
    let assigned = 0;
    let failed = 0;
    for (const choice of unmappedChoices) {
      const source = findTracifySource(choice);
      try {
        await createMapping.mutateAsync({
          projectId: project.id,
          csid: project.csid,
          answer_value: choice,
          ...(source
            ? { tracify_source: source }
            : {
                virtual_channel_name: formatChannelName(choice.trim()),
              }),
        });
        assigned++;
      } catch {
        failed++;
      }
    }
    setAutoAssigning(false);
    if (assigned > 0)
      toast.success(
        `Auto-assigned ${assigned} mapping${assigned !== 1 ? "s" : ""}.`
      );
    if (failed > 0)
      toast.error(
        `${failed} mapping${
          failed !== 1 ? "s" : ""
        } failed (may already exist).`
      );
  };

  useEffect(() => {
    if (
      isLoading ||
      blocksLoading ||
      hasAutoAssigned.current ||
      unmappedChoices.length === 0
    )
      return;
    hasAutoAssigned.current = true;
    handleAutoAssign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, blocksLoading]);

  const columns = useMemo<ColumnDef<DecifyMapping>[]>(
    () => [
      {
        accessorKey: "answer_value",
        header: "Survey answer",
        size: 300,
        cell: ({ getValue }) => (
          <span className="font-mono text-xs">{getValue() as string}</span>
        ),
      },
      {
        id: "type",
        header: "Type",
        size: 160,
        accessorFn: (row) =>
          row.tracify_source ? "Ad source" : "Custom label",
        cell: ({ getValue }) => (
          <span className="text-xs text-muted-foreground">
            {getValue() as string}
          </span>
        ),
      },
      {
        id: "channel",
        header: "Channel",
        size: 300,
        accessorFn: (row) =>
          row.tracify_source
            ? getTracifySourceLabel(row.tracify_source).toLowerCase()
            : (row.virtual_channel_name ?? ""),
        cell: ({ row: { original: m } }) => (
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs">
              {m.tracify_source
                ? getTracifySourceLabel(m.tracify_source)
                    .toLowerCase()
                    .replace(/^\w/, (c) => c.toUpperCase())
                : m.virtual_channel_name}
            </span>
            {m.tracify_source && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 dark:border-violet-800 dark:bg-violet-950 dark:text-violet-300">
                    <svg
                      width="10"
                      height="9"
                      viewBox="0 0 21 19"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="shrink-0"
                    >
                      <path
                        d="M20.6933 0.857666H0.687134L2.10769 3.28445H4.59366L3.35068 5.47447L10.6902 18.2003L18.6808 4.29068H15.8397L9.92075 14.7081H8.73695L14.6559 4.29068H11.9332L7.8491 11.2751H6.78369L10.6902 4.29068H7.96748L5.95503 7.84207H4.83042L7.43478 3.28445H19.2727L20.6933 0.857666Z"
                        fill="currentColor"
                      />
                    </svg>
                    tracify
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  This survey answer is mapped to a Tracify ad source channel.
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        size: 80,
        cell: ({ row: { original: m } }) => (
          <div className="flex items-center gap-1">
            <button
              type="button"
              className="flex items-center justify-center h-7 w-7 rounded text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              onClick={() => setEditingMapping(m)}
              title="Edit mapping"
            >
              <PencilSquareIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              className="flex items-center justify-center h-7 w-7 rounded text-destructive/60 hover:text-destructive hover:bg-destructive/10 transition-colors"
              onClick={() => handleDelete(m.id)}
              title="Remove mapping"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        ),
      },
    ],
    [handleDelete]
  );

  return (
    <div className="space-y-3">
      {!blocksLoading && answerChoices.length === 0 ? (
        <div className="flex items-start gap-2.5 rounded-md border bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800/40 px-3 py-2.5 text-xs text-amber-700 dark:text-amber-400">
          <ExclamationCircleIcon className="w-4 h-4 shrink-0 mt-0.5" />
          <p>
            No survey answers found. Sync the project to pull the latest answers
            — or add them in Decify first if the survey has no responses yet.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">Channel mappings</h4>
            <div className="flex gap-2">
              {unmappedChoices.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleAutoAssign}
                  disabled={autoAssigning}
                >
                  <ArrowPathIcon
                    className={`w-4 h-4 mr-1.5 ${
                      autoAssigning ? "animate-spin" : ""
                    }`}
                  />
                  {autoAssigning ? "Assigning…" : "Auto assign"}
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDialogOpen(true)}
              >
                <PlusIcon className="w-4 h-4 mr-1.5" />
                Add mapping
              </Button>
            </div>
          </div>

          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : mappings.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No mappings yet. Add one to start routing survey responses to
              channels.
            </p>
          ) : (
            <TableComponent
              data={mappings}
              columns={columns}
              hidePagination
              spacing="tight"
              defaultSorting={[{ id: "type", desc: false }]}
            />
          )}

          <AddMappingDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            project={project}
            answerChoices={answerChoices}
            mappedAnswers={mappedAnswers}
          />
          {editingMapping && (
            <EditMappingDialog
              open={!!editingMapping}
              onOpenChange={(v) => {
                if (!v) setEditingMapping(null);
              }}
              project={project}
              mapping={editingMapping}
            />
          )}
        </>
      )}
    </div>
  );
}

// --- Single project card ---

function ProjectCard({ project }: { project: DecifyProject }) {
  const patch = usePatchDecifyProject();
  const remove = useDeleteDecifyProject();
  const syncBlocks = useSyncDecifyBlocks();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleDelete = async () => {
    try {
      await remove.mutateAsync({ id: project.id, csid: project.csid });
      toast.success("Project removed.");
    } catch {
      toast.error("Failed to remove project.");
      setConfirmDelete(false);
    }
  };

  const handleSync = async () => {
    try {
      await syncBlocks.mutateAsync({
        projectId: project.id,
        csid: project.csid,
      });
      toast.success("Blocks synced.");
    } catch {
      toast.error("Failed to sync blocks.");
    }
  };

  return (
    <Card className="bg-background">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="min-w-0 space-y-1">
              <p className="text-sm font-semibold leading-tight">
                {project.survey_title || "Untitled survey"}
              </p>
              <div className="mt-2 rounded-md border bg-muted/40 px-3 py-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span className="text-foreground/50">Project ID</span>
                <span className="font-mono truncate">{project.project_id}</span>
                <span className="text-foreground/50">Last polled</span>
                <span>
                  {project.last_polled_at
                    ? dayjs(project.last_polled_at).format("MMM D, YYYY HH:mm")
                    : "—"}
                </span>
              </div>
              {project.unmapped_answers_count > 0 && (
                <div className="flex items-center gap-1.5 mt-1">
                  <ExclamationCircleIcon className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                  <p className="text-xs text-amber-500">
                    {project.unmapped_answers_count} unmapped answer
                    {project.unmapped_answers_count !== 1 ? "s" : ""} — add
                    mappings to capture responses
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {confirmDelete ? (
              <>
                <span className="text-xs text-muted-foreground mr-1">
                  Remove?
                </span>
                <Button
                  variant="ghost"
                  className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={handleDelete}
                  disabled={remove.isPending}
                >
                  {remove.isPending ? "Removing…" : "Yes"}
                </Button>
                <Button
                  variant="ghost"
                  className="h-7 px-2 text-xs"
                  onClick={() => setConfirmDelete(false)}
                  disabled={remove.isPending}
                >
                  No
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  className="h-7 w-7 p-0"
                  title="Sync survey blocks"
                  onClick={handleSync}
                  disabled={syncBlocks.isPending}
                >
                  <ArrowPathIcon
                    className={`w-3.5 h-3.5 ${
                      syncBlocks.isPending ? "animate-spin" : ""
                    }`}
                  />
                </Button>
                <Button
                  variant="ghost"
                  className="h-7 w-7 text-destructive/60 hover:text-destructive hover:bg-destructive/10 p-0"
                  title="Remove project"
                  onClick={() => setConfirmDelete(true)}
                >
                  <TrashIcon className="w-3.5 h-3.5" />
                </Button>
              </>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="border-t pt-4">
        <MappingsSection project={project} />
      </CardContent>
    </Card>
  );
}

// --- Connect project modal ---

function ConnectProjectModal({
  open,
  onOpenChange,
  organisationId,
  csid,
  existingProjects,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organisationId: string;
  csid: string;
  existingProjects: DecifyProject[];
}) {
  const [projectId, setProjectId] = useState("");
  const [surveyId, setSurveyId] = useState("");
  const [fetchEnabled, setFetchEnabled] = useState(false);
  const [manualProjectId, setManualProjectId] = useState(false);

  const {
    data: surveys = [],
    isFetching,
    isError,
  } = useDecifySurveys(projectId, csid, fetchEnabled && !!projectId);
  const { data: appInfo, isError: appInfoError } = useDecifyAppInfo(csid, open);
  const createProject = useCreateDecifyProject();
  const existingSurveyIds = new Set(existingProjects.map((p) => p.survey_id));

  useEffect(() => {
    if (appInfo?.project_id && !projectId) {
      setProjectId(appInfo.project_id);
      setFetchEnabled(true);
    }
  }, [appInfo?.project_id, projectId]);

  const reset = () => {
    setProjectId("");
    setSurveyId("");
    setFetchEnabled(false);
    setManualProjectId(false);
  };

  const handleProjectIdChange = (value: string) => {
    setProjectId(value);
    setFetchEnabled(false);
    setSurveyId("");
  };

  const handleProjectIdSelectChange = (value: string) => {
    if (value === "__manual__") {
      setManualProjectId(true);
      setProjectId("");
      setFetchEnabled(false);
      setSurveyId("");
    } else {
      handleProjectIdChange(value);
    }
  };

  const showProjectIdSelect = !!appInfo?.project_id && !manualProjectId;

  const handleConnect = async (overrideSurveyId?: string) => {
    const id = overrideSurveyId ?? surveyId;
    if (!id) return;
    try {
      await createProject.mutateAsync({
        csid,
        project_id: projectId,
        survey_id: id,
        organisation_id: organisationId,
      });
      toast.success("Project connected. Survey blocks synced automatically.");
      reset();
      onOpenChange(false);
    } catch {
      toast.error(
        "Failed to connect project. This CSID may already be connected."
      );
    }
  };

  useEffect(() => {
    if (!fetchEnabled || isFetching || isError || surveys.length !== 1) return;
    const survey = surveys[0];
    if (existingSurveyIds.has(survey.id)) return;
    handleConnect(survey.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchEnabled, isFetching, isError, surveys]);

  const handleClose = (v: boolean) => {
    if (!v) reset();
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Connect project</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Decify project ID</Label>
            {showProjectIdSelect ? (
              <Select
                value={projectId}
                onValueChange={handleProjectIdSelectChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a project…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={appInfo.project_id}>
                    <span className="font-mono text-xs">
                      {appInfo.project_id}
                    </span>
                  </SelectItem>
                  <SelectItem value="__manual__">
                    Enter a different ID…
                  </SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <>
                <Input
                  placeholder="65c0b1b554…"
                  value={projectId}
                  onChange={(e) => handleProjectIdChange(e.target.value)}
                />
                {appInfoError && (
                  <p className="text-xs text-muted-foreground">
                    Could not pre-fill project ID
                  </p>
                )}
              </>
            )}
          </div>

          {isFetching && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <ArrowPathIcon className="w-4 h-4 animate-spin shrink-0" />
              Fetching surveys…
            </div>
          )}

          {fetchEnabled &&
            !isFetching &&
            (isError ? (
              <p className="text-sm text-destructive">
                Failed to fetch surveys. Check the project ID and your API
                credentials.
              </p>
            ) : surveys.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No surveys found for this project.
              </p>
            ) : (
              <div className="space-y-1.5">
                <Label>Survey</Label>
                <Select value={surveyId} onValueChange={setSurveyId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a survey…" />
                  </SelectTrigger>
                  <SelectContent>
                    {surveys.map((s) => {
                      const alreadyConnected = existingSurveyIds.has(s.id);
                      return (
                        <SelectItem
                          key={s.id}
                          value={s.id}
                          textValue={s.name}
                          disabled={alreadyConnected}
                        >
                          <span
                            className={
                              alreadyConnected
                                ? "text-muted-foreground line-through"
                                : undefined
                            }
                          >
                            {s.name}
                          </span>
                          {alreadyConnected && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              already connected
                            </span>
                          )}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!fetchEnabled) {
                if (projectId.trim()) setFetchEnabled(true);
              } else {
                handleConnect();
              }
            }}
            disabled={
              !projectId.trim() ||
              isFetching ||
              createProject.isPending ||
              (fetchEnabled &&
                !isFetching &&
                !isError &&
                surveys.length > 0 &&
                !surveyId)
            }
          >
            {isFetching
              ? "Fetching…"
              : createProject.isPending
                ? "Connecting…"
                : "Connect"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Projects section ---

function ProjectsSection({
  organisationId,
  csid,
  hasCredentials,
  disableConnect,
}: {
  organisationId: string;
  csid: string;
  hasCredentials: boolean;
  disableConnect?: boolean;
}) {
  const { data: projects = [], isLoading } = useDecifyProjects(csid);
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">Projects</h3>
          <p className="text-sm text-muted-foreground">
            Connect a Tracify store to a Decify survey.
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setModalOpen(true)}
          disabled={!hasCredentials || disableConnect}
        >
          <PlusIcon className="w-4 h-4 mr-1" />
          Connect project
        </Button>
      </div>

      <ConnectProjectModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        organisationId={organisationId}
        csid={csid}
        existingProjects={projects}
      />

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading projects…</p>
      ) : projects.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No projects connected yet.
        </p>
      ) : (
        <div className="space-y-4">
          {projects.map((p) => (
            <ProjectCard key={p.id} project={p} />
          ))}
        </div>
      )}
    </div>
  );
}

// --- Per-store content ---

function DecifyStoreContent({
  csid,
  organisationId,
  shopIntegrations,
}: {
  csid: string;
  organisationId: string;
  shopIntegrations: { csid: string; type: string }[];
}) {
  const {
    data: creds,
    isError: credsError,
    isLoading: credsLoading,
  } = useDecifyCredentials(csid);
  const { data: projects = [] } = useDecifyProjects(csid);

  const hasCredentials = !credsError && !!creds;
  const hasIntegrationError = projects.some((p) => !!p.last_poll_error);
  const hasShopifyIntegration = shopIntegrations.some(
    (i) => i.csid === csid && i.type.startsWith("shopify")
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex gap-4 items-center">
              <div className="bg-muted w-12 h-12 flex items-center justify-center rounded-lg shrink-0">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 41 41"
                  fill="currentColor"
                  className="text-foreground"
                >
                  <path d="M0 0V15.0243H25.0406V40.0649H40.0649V0H0Z" />
                  <path d="M15.0243 25.0406H0V40.0649H15.0243V25.0406Z" />
                </svg>
              </div>
              <p className="h4 text-foreground">Decify</p>
            </div>
            {!credsLoading &&
              (!hasCredentials ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="h-5 w-5 rounded-full border border-dashed border-muted-foreground" />
                  <span>Not configured</span>
                </div>
              ) : hasIntegrationError ? (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <ExclamationTriangleIcon className="w-5 h-5" />
                  <span>Disconnected</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircleIconSolid className="text-green-500 w-6 h-6" />
                  <span>Connected</span>
                </div>
              ))}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {!hasShopifyIntegration ? (
            <div className="flex items-center gap-2 rounded-md border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-950/20 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
              <ExclamationTriangleIcon className="w-4 h-4 shrink-0" />
              <span>
                A Shopify integration is required to use Decify.{" "}
                <a
                  href="/settings/shop-system/shopify"
                  className="underline font-medium"
                >
                  Set it up here.
                </a>
              </span>
            </div>
          ) : (
            hasIntegrationError && (
              <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                <ExclamationTriangleIcon className="w-4 h-4 shrink-0" />
                There is a problem with the Decify integration. Survey responses
                are not being collected. Check your API credentials.
              </div>
            )
          )}
          <p className="text-sm text-muted-foreground">
            Connect Decify post-purchase surveys to Tracify. Survey responses
            are polled every 5 minutes and mapped to channels automatically.
          </p>
          <CredentialsSection
            csid={csid}
            hasIntegrationError={hasIntegrationError}
            disabled={!hasShopifyIntegration}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <ProjectsSection
            organisationId={organisationId}
            csid={csid}
            hasCredentials={hasCredentials}
            disableConnect={!hasShopifyIntegration}
          />
        </CardContent>
      </Card>
    </div>
  );
}

// --- Root ---

export function DecifySettings() {
  const { selectedOrganisation, selectedStores } = useSelectedStores();
  const organisationId = selectedOrganisation ?? "";
  const { data: shopIntegrations = [] } = useShopIntegrations();

  if (selectedStores.length > 1) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            Decify can only be configured for one store at a time. Please select
            a single store to manage its Decify settings.
          </p>
        </CardContent>
      </Card>
    );
  }

  const csid = selectedStores[0]?.csid ?? "";
  return (
    <DecifyStoreContent
      csid={csid}
      organisationId={organisationId}
      shopIntegrations={shopIntegrations}
    />
  );
}
